// PATO across all of an employee's saved vacation plans: where the balance
// stands today, what each plan costs, and where it would be at the end of the
// year if every plan still ahead were taken.
//
// A trip already behind us is not counted from its plan. Its PATO is in the
// balance already - TimeCamp has it - so what the card shows is the actual
// PATO booked on its dates, and the projection does not take it off again.
// A trip under way is split: what has been booked so far is actual, and only
// the rest of the planned PATO is still to come.
//
// Pure: the page passes in the plans, the PATO reports (one per year, from
// /payroll/pato/:id/report) and the employee's own time-off requests.

import { isPatoRequest, plannerAllocationForRequest, requestHoursByDay } from './vacationPlan';

const round2 = (value) => Math.round(((Number(value) || 0) + Number.EPSILON) * 100) / 100;

const yearOf = (iso) => Number(String(iso).slice(0, 4));

const isWeekday = (date) => {
  const day = date.getUTCDay();
  return day !== 0 && day !== 6;
};

const plannedPatoByDate = (plan, planned) => {
  const saved = plan.summary?.patoByDate;
  if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
    return new Map(Object.entries(saved)
      .map(([date, hours]) => [date.slice(0, 10), round2(hours)])
      .filter(([date, hours]) => /^\d{4}-\d{2}-\d{2}$/.test(date) && hours > 0));
  }

  const start = new Date(`${String(plan.start_date).slice(0, 10)}T00:00:00Z`);
  const end = new Date(`${String(plan.end_date).slice(0, 10)}T00:00:00Z`);
  const days = [];
  for (const date = new Date(start); date <= end; date.setUTCDate(date.getUTCDate() + 1)) {
    if (isWeekday(date)) days.push(date.toISOString().slice(0, 10));
  }
  const byDate = new Map();
  if (days.length > 0 && planned > 0) {
    const perDay = round2(planned / days.length);
    let assigned = 0;
    days.forEach((date, index) => {
      const hours = index === days.length - 1 ? round2(planned - assigned) : perDay;
      assigned = round2(assigned + perDay);
      if (hours > 0) byDate.set(date, hours);
    });
  }
  return byDate;
};

const bookedPatoByDate = (reportsByYear) => {
  const byDate = new Map();
  reportsByYear.forEach((report) => {
    (report?.events || []).forEach((event) => {
      if (event.kind !== 'pato' || !event.date) return;
      const date = String(event.date).slice(0, 10);
      byDate.set(date, (byDate.get(date) || 0) + (Number(event.hours) || 0));
    });
  });
  return byDate;
};

// PATO booked (time off, not sick) on [from, to], across however many years
// that spans. Null when a year it needs has no usage to read.
export const patoBookedBetween = (fromIso, toIso, reportsByYear) => {
  let total = 0;
  for (let year = yearOf(fromIso); year <= yearOf(toIso); year += 1) {
    const report = reportsByYear.get(year);
    if (!report || report.usageKnown === false || !Array.isArray(report.events)) return null;
    for (const event of report.events) {
      if (event.kind !== 'pato' || !event.date) continue;
      if (event.date >= fromIso && event.date <= toIso) total += Number(event.hours) || 0;
    }
  }
  return round2(total);
};

/**
 * @param {object} args
 * @param {Array}  args.plans          saved plans (start_date, end_date, summary, submission)
 * @param {Map}    args.reportsByYear  year -> PATO report
 * @param {Array}  args.requests       the employee's time-off requests
 * @param {string} args.todayIso       YYYY-MM-DD
 */
export const buildPatoOutlook = ({
  plans = [],
  reportsByYear = new Map(),
  requests = [],
  closeKeys = new Set(),
  todayIso,
}) => {
  const currentYear = yearOf(todayIso);
  const report = reportsByYear.get(currentYear) || null;
  const unlimited = Boolean(report?.unlimited);
  const balanceToday = report && report.remainingHours != null ? round2(report.remainingHours) : null;

  // Requests a plan made are that plan's, not "other" time off.
  const planRequestIds = new Set();
  const planIds = new Set();
  plans.forEach((plan) => {
    planIds.add(String(plan.id));
    (plan.submission?.requestIds || []).forEach((id) => planRequestIds.add(String(id)));
  });

  const perPlan = new Map();
  const ahead = [];
  const bookedByDate = bookedPatoByDate(reportsByYear);

  plans.forEach((plan) => {
    const start = String(plan.start_date).slice(0, 10);
    const end = String(plan.end_date).slice(0, 10);
    const planned = round2(plan.summary?.patoHours ?? 0);
    const plannedByDate = plannedPatoByDate(plan, planned);
    let timing = 'upcoming';
    if (end < todayIso) timing = 'past';
    else if (start <= todayIso) timing = 'ongoing';

    let actual = null;
    let stillToCome = planned;
    if (timing === 'past') {
      actual = patoBookedBetween(start, end, reportsByYear);
      stillToCome = 0;
    } else if (timing === 'ongoing') {
      actual = patoBookedBetween(start, todayIso, reportsByYear);
    }

    const remainingByDate = new Map();
    plannedByDate.forEach((hours, date) => {
      if (date < todayIso) return;
      const remaining = round2(Math.max(0, hours - (bookedByDate.get(date) || 0)));
      if (remaining > 0) remainingByDate.set(date, remaining);
    });
    if (timing !== 'past') {
      stillToCome = round2([...remainingByDate.values()].reduce((sum, hours) => sum + hours, 0));
    }

    const firstRemainingDate = [...remainingByDate.keys()].sort()[0];
    const entry = {
      timing,
      plannedPato: planned,
      actualPato: actual,
      stillToCome,
      // Which year's balance the PATO still to come comes out of.
      balanceYear: firstRemainingDate ? yearOf(firstRemainingDate) : yearOf(end),
      // The same, date by date - for a caller that needs one year's share of
      // a trip that crosses into the next.
      remainingByDate: Object.fromEntries(remainingByDate),
      balanceAfter: null,
    };
    perPlan.set(plan.id, entry);

    remainingByDate.forEach((hours, date) => {
      if (yearOf(date) === currentYear) ahead.push({ date, hours, planId: plan.id });
    });
  });

  // PATO already asked for outside any plan, still ahead this year. Not in
  // TimeCamp yet, so not in today's balance either.
  let otherRequestedHours = 0;
  requests.forEach((request) => {
    if (!isPatoRequest(request)) return;
    if (planRequestIds.has(String(request.id))) return;
    if (planIds.has(String(plannerAllocationForRequest(request)?.planId || ''))) return;
    requestHoursByDay(request, closeKeys).forEach((hours, date) => {
      if (date < todayIso || yearOf(date) !== currentYear) return;
      const remaining = round2(Math.max(0, hours - (bookedByDate.get(date) || 0)));
      if (remaining <= 0) return;
      otherRequestedHours += remaining;
      ahead.push({ date, hours: remaining, planId: null });
    });
  });

  // The balance walked forward through everything ahead, in date order, so
  // each plan can say where it leaves things.
  ahead.sort((a, b) => a.date.localeCompare(b.date));
  let running = balanceToday;
  if (running != null && !unlimited) {
    ahead.forEach((item) => {
      running = round2(running - item.hours);
      if (item.planId != null) perPlan.get(item.planId).balanceAfter = running;
    });
  }

  const plannedAhead = round2(ahead.filter((item) => item.planId != null).reduce((sum, item) => sum + item.hours, 0));
  return {
    currentYear,
    unlimited,
    usageKnown: report ? report.usageKnown !== false : false,
    balanceToday,
    plannedAhead,
    otherRequestedHours: round2(otherRequestedHours),
    yearEnd: balanceToday == null || unlimited
      ? null
      : round2(balanceToday - plannedAhead - otherRequestedHours),
    perPlan,
  };
};
